import React, { useState } from 'react';
import { FLAGS } from '../../calendar/mockData';
import type { BirdEyeDragSource } from '../../bird-eye/boardTypes';
import type { BirdEyePatient } from '../../bird-eye/types';
import { BADGE_TONE_COLOR, cellTone, formatMoney, hueFor, TONE_STYLES } from '../../bird-eye/utils';
import { cn } from '../../lib/cn';
import { Avatar } from '../Avatar/Avatar';
import { Icon } from '../Icon/Icon';
import { useBirdEyeDnd } from './BirdEyeDndContext';

export interface BirdEyePatientCardProps {
  patient: BirdEyePatient;
  dragSource?: BirdEyeDragSource;
}

export function BirdEyePatientCard({ patient: p, dragSource }: BirdEyePatientCardProps) {
  const [hovered, setHovered] = useState(false);
  const dnd = useBirdEyeDnd();
  const tone = cellTone(p.flags);
  const tc = TONE_STYLES[tone].fg;
  const bt = BADGE_TONE_COLOR[p.bt] ?? BADGE_TONE_COLOR.neutral;
  const toneFlag = p.flags.find((f) => FLAGS[f]?.tone === tone);
  const toneFlagMeta = toneFlag ? FLAGS[toneFlag] : undefined;
  const isDragging = dragSource ? dnd.isDraggingSource(dragSource) : false;

  return (
    <div
      draggable={Boolean(dragSource)}
      onDragStart={
        dragSource
          ? (e) => {
              dnd.onDragStart(dragSource, e);
            }
          : undefined
      }
      onDragEnd={dragSource ? dnd.onDragEnd : undefined}
      onMouseEnter={() => {
        setHovered(true);
      }}
      onMouseLeave={() => {
        setHovered(false);
      }}
      className={cn(
        'rounded-xl border border-[var(--cream-2)] bg-[var(--cream-1)] px-3 py-[11px] shadow-[var(--sh-1)] transition-[transform,box-shadow,opacity] duration-[140ms]',
        dragSource ? 'cursor-grab active:cursor-grabbing' : 'cursor-default',
        isDragging && 'be-card-dragging',
        hovered && !isDragging && '-translate-y-px shadow-[var(--sh-2)]',
      )}
      style={{ borderLeftWidth: 3, borderLeftColor: tc }}
    >
      <div className="mb-2 flex items-center gap-[9px]">
        <Avatar name={p.patient} hue={hueFor(p.patient)} size={30} />
        <div className="min-w-0 flex-1">
          <div className="truncate text-[13px] font-semibold text-[var(--ink-0)]">{p.patient}</div>
          <div className="truncate text-[10.5px] text-[var(--ink-3)]">
            {p.appt ? `${p.appt} · ` : ''}
            {p.prov}
          </div>
        </div>
        <span
          className="h-[7px] w-[7px] shrink-0 rounded-full"
          style={{ background: tc }}
          title={toneFlagMeta?.label}
        />
      </div>
      <div className="truncate text-xs font-medium text-[var(--ink-1)]">{p.proc}</div>
      <div className="mt-2 flex flex-wrap items-center gap-[5px]">
        {p.flags.slice(0, 3).map((fk) => {
          const f = FLAGS[fk];
          if (!f) return null;
          const ft = TONE_STYLES[f.tone].fg;
          return (
            <span
              key={fk}
              title={f.label}
              className="inline-flex h-[17px] w-[17px] items-center justify-center rounded-[5px]"
              style={{
                background: TONE_STYLES[f.tone].tint,
                color: ft,
              }}
            >
              <Icon kind={f.icon} size={9.5} stroke={2} />
            </span>
          );
        })}
        <span className="ml-auto font-[var(--font-mono)] text-[11px] font-semibold text-[var(--ink-2)]">
          {formatMoney(p.value)}
        </span>
      </div>
      <div
        className="mt-2 inline-flex items-center gap-[5px] rounded-[var(--r-pill)] px-[9px] py-[3px] text-[10.5px] font-bold"
        style={{
          background: `color-mix(in srgb, ${bt} 13%, var(--cream-1))`,
          color: bt,
        }}
      >
        <Icon kind="clock" size={10} stroke={2} />
        {p.badge}
      </div>
    </div>
  );
}
